<script setup lang="ts">
import { ref, computed } from "vue";
import type { Legend } from "@/types/legend.types";
import { FileType } from "@/types/file.types";
import { uploadFilesBatch } from "@/utils/upload-files.utils";
import { useLinkLegendDwg, useUnlinkLegendDwg } from "@/stores/queries";
import DRConfirmPopup from "@/components/UI/feedbacks/DRConfirmPopup.vue";

const MAX_SIZE_BYTES = 1024 * 1024; // 1 Mo

const props = defineProps<{ legend: Legend }>();
const emits = defineEmits<{ (e: "updated"): void }>();

const fileInput = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);
const errorMsg = ref<string | null>(null);

const legendId = computed(() => props.legend.id);
const dwgInfo = computed(() => props.legend.dwg_file_info);

const { mutateAsync: linkDwg } = useLinkLegendDwg(legendId);
const { mutateAsync: unlinkDwg, isPending: isDeleting } = useUnlinkLegendDwg(legendId);

function formatSize(bytes: number): string {
  return bytes < 1024
    ? `${bytes} o`
    : `${(bytes / 1024).toFixed(1)} Ko`;
}

function openPicker() {
  fileInput.value?.click();
}

async function onFileChange(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = "";
  if (!file) return;

  errorMsg.value = null;

  if (file.size > MAX_SIZE_BYTES) {
    errorMsg.value = `Fichier trop volumineux (max 1 Mo, fichier : ${formatSize(file.size)})`;
    return;
  }

  isUploading.value = true;
  try {
    const results = await uploadFilesBatch([
      {
        file,
        fileType: FileType.LEGEND_DWG,
        params: { legendId: legendId.value },
      },
    ]);
    await linkDwg(results[0].fileId);
    emits("updated");
  } catch (err) {
    errorMsg.value = "Erreur lors de l'upload. Réessayez.";
    console.error(err);
  } finally {
    isUploading.value = false;
  }
}

async function handleUnlink() {
  await unlinkDwg();
  emits("updated");
}
</script>

<template>
  <div class="legend-dwg-manager">
    <h6 class="fw-semibold mb-2">Bibliothèque de blocs DWG</h6>
    <p class="text-muted small mb-3">
      Associez un fichier DWG (max&nbsp;1&nbsp;Mo) contenant vos blocs. Il sera disponible offline dans l'outil de dessin.
    </p>

    <input
      ref="fileInput"
      type="file"
      accept=".dwg"
      class="d-none"
      @change="onFileChange"
    />

    <!-- DWG existant -->
    <div v-if="dwgInfo" class="dwg-info d-flex align-items-center gap-2 mb-3 p-2 rounded border">
      <i class="bi bi-file-earmark-code fs-5 text-primary"></i>
      <div class="flex-grow-1 overflow-hidden">
        <div class="text-truncate small fw-medium">{{ dwgInfo.originalName }}</div>
        <div class="text-muted" style="font-size: 0.75rem">{{ formatSize(dwgInfo.size) }}</div>
      </div>
      <a
        v-if="dwgInfo.download_url"
        :href="dwgInfo.download_url"
        :download="dwgInfo.originalName"
        class="btn btn-sm btn-outline-secondary"
        title="Télécharger"
      >
        <i class="bi bi-download"></i>
      </a>
      <button
        class="btn btn-sm btn-outline-secondary"
        @click="openPicker"
        :disabled="isUploading"
        title="Remplacer"
      >
        <i class="bi bi-arrow-repeat"></i>
      </button>
      <DRConfirmPopup title="Supprimer le DWG de blocs ?" @confirm="handleUnlink">
        <button
          class="btn btn-sm btn-outline-danger"
          :disabled="isDeleting"
          title="Supprimer"
        >
          <i class="bi bi-trash3"></i>
        </button>
      </DRConfirmPopup>
    </div>

    <!-- Pas de DWG -->
    <button
      v-else
      class="btn btn-sm btn-outline-primary mb-3"
      @click="openPicker"
      :disabled="isUploading"
    >
      <span v-if="isUploading">
        <span class="spinner-border spinner-border-sm me-1"></span>Upload en cours…
      </span>
      <span v-else>
        <i class="bi bi-upload me-1"></i>Uploader un DWG de blocs
      </span>
    </button>

    <div v-if="isUploading && dwgInfo" class="text-muted small mb-2">
      <span class="spinner-border spinner-border-sm me-1"></span>Remplacement en cours…
    </div>

    <div v-if="errorMsg" class="alert alert-danger py-1 px-2 small mb-0">
      {{ errorMsg }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dwg-info {
  background: var(--bs-light);
  border-color: var(--bs-border-color) !important;
}
</style>
